import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allCharts, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

/**
 * Standardtitel, die Excel selbst einsetzt.
 *
 * Excel benennt ein neues Diagramm nach der Datenreihe oder schreibt schlicht
 * „Diagrammtitel“ hinein. Beides ist keine Aussage, sondern ein Platzhalter.
 */
function istPlatzhaltertitel(titel: string, reihenName: string | null): boolean {
  const bereinigt = titel.trim();
  if (/^(diagrammtitel|chart title|titel|title)$/i.test(bereinigt)) return true;
  if (reihenName !== null && bereinigt.toLowerCase() === reihenName.trim().toLowerCase()) return true;
  return bereinigt.length === 0;
}

export const lektionLesbar: Lesson = {
  id: "lesbar",
  title: "Ein Diagramm lesbar machen",
  summary: "Der Titel sagt, was zu sehen ist – nicht, was aufgetragen wurde.",
  durationMinutes: 10,
  learningGoals: [
    "Einen Diagrammtitel schreiben, der eine Aussage trifft",
    "Achsen und Legende bewusst einsetzen oder weglassen",
    "Überflüssige Elemente aus einem Diagramm entfernen",
  ],
  steps: [
    {
      id: "aussagetitel",
      title: "Der Titel ist die Aussage",
      explanation: [
        "„Umsatz nach Region“ beschreibt, was aufgetragen wurde. „Der Süden trägt die " +
          "Hälfte des Umsatzes“ sagt, was man sehen soll. Der zweite Titel ist die " +
          "bessere Wahl, weil er dem Betrachter die Arbeit abnimmt, aus dem Diagramm " +
          "die Botschaft zu destillieren – und weil er verrät, ob der Autor selbst " +
          "eine hatte.",
        "Diese Regel gilt für jedes Diagramm, das jemand anderem gezeigt wird. Für " +
          "ein Arbeitsdiagramm, das man selbst beim Analysieren benutzt, ist ein " +
          "beschreibender Titel völlig in Ordnung. Der Unterschied liegt im Zweck: " +
          "erkunden oder erklären.",
        "Was auf keinen Fall stehen bleiben sollte, ist der Titel, den Excel selbst " +
          "einsetzt – „Diagrammtitel“ oder schlicht der Name der Datenreihe. Beides " +
          "signalisiert, dass niemand nachgedacht hat, und kostet mehr Vertrauen, als " +
          "gar kein Titel es täte.",
        "Der Titel lässt sich auch an eine Zelle koppeln: Titel anklicken, dann in " +
          "der Bearbeitungsleiste =B1 eintippen. Dann steht dort immer der Inhalt " +
          "dieser Zelle – praktisch, wenn der Titel eine Zahl enthält, die sich " +
          "ändert.",
      ],
      keyPoints: [
        "Ein Titel für andere trifft eine Aussage, nicht nur eine Beschreibung.",
        "Titel anklicken und =B1 eintippen koppelt ihn an eine Zelle.",
        "„Diagrammtitel“ stehen zu lassen, ist schlimmer als gar kein Titel.",
      ],
      task: {
        instruction:
          "Gib deinem Diagramm einen Titel, der eine Aussage über die Daten trifft – " +
          "nicht bloß „Diagrammtitel“ oder den Namen der Datenreihe.",
        hint: "Doppelklick auf den Titel im Diagramm, dann den Text überschreiben.",
        check: async () => {
          const mappe = await readWorkbook();
          const diagramme = allCharts(mappe);

          if (diagramme.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch kein Diagramm.",
              "Löse zuerst die Aufgabe aus der vorigen Lektion.",
            );
          }

          const lesbar = diagramme.filter(
            (diagramm) => diagramm.title !== null || diagramm.titleVisible !== null,
          );
          if (lesbar.length === 0) {
            return nichtPruefbar(
              "Der Titel eines Diagramms",
              "1.1",
              "Sieh nach, ob über deinem Diagramm ein aussagekräftiger Titel steht.",
            );
          }

          const ohneTitel = diagramme.filter(
            (diagramm) => diagramm.title === null || diagramm.titleVisible === false,
          );
          if (ohneTitel.length > 0) {
            return fail(
              `Das Diagramm „${ohneTitel[0].name}“ trägt keinen sichtbaren Titel.`,
              "Klicke das Diagramm an, dann auf das Pluszeichen rechts daneben und setze das Häkchen " +
                "bei „Diagrammtitel“. Danach den Text überschreiben.",
            );
          }

          const platzhalter = diagramme.filter(
            (diagramm) =>
              diagramm.title !== null &&
              istPlatzhaltertitel(diagramm.title, diagramm.seriesCount === 1 ? diagramm.name : null),
          );
          if (platzhalter.length > 0) {
            return fail(
              `Das Diagramm „${platzhalter[0].name}“ trägt noch den Titel ` +
                `„${platzhalter[0].title}“ – das ist ein Platzhalter, keine Aussage.`,
              "Doppelklick auf den Titel und überschreiben. Schreibe, was man sehen soll: " +
                "„Der Süden trägt die Hälfte des Umsatzes“ statt „Umsatz nach Region“.",
            );
          }

          return pass(
            `„${diagramme[0].title}“ – ein Titel, der etwas über die Daten sagt.` +
              (diagramme.length > 1 ? ` Alle ${diagramme.length} Diagramme sind betitelt.` : ""),
          );
        },
      },
    },
    {
      id: "aufraeumen",
      title: "Weglassen ist die stärkste Bearbeitung",
      explanation: [
        "Ein neues Diagramm bringt mehr mit, als es braucht: Gitternetzlinien, eine " +
          "Legende, oft eine Achse mit unnötig vielen Zwischenschritten. Jedes dieser " +
          "Elemente kostet Aufmerksamkeit. Die beste Bearbeitung eines Diagramms " +
          "besteht meistens darin, Dinge zu entfernen.",
        "Die Legende zuerst: Bei einer einzigen Datenreihe ist sie überflüssig, denn " +
          "sie wiederholt nur, was schon im Titel steht. Bei zwei oder drei Reihen ist " +
          "eine Beschriftung direkt an der Linie besser als eine Legende, weil das Auge " +
          "nicht hin- und herwandern muss. Erst ab vier Reihen lohnt die Legende " +
          "wirklich.",
        "Gitternetzlinien braucht es nur, wenn Werte abgelesen werden sollen. Geht es " +
          "um den Vergleich – und darum geht es meistens –, stören sie. Wenn genaue " +
          "Werte wichtig sind, sind Datenbeschriftungen direkt an den Säulen ohnehin " +
          "die bessere Lösung als Gitternetz plus Achse.",
        "Ein Punkt, an dem Diagramme zu Lügen werden: die abgeschnittene Wertachse. " +
          "Beginnt die Achse bei 95 statt bei 0, sieht ein Unterschied von zwei " +
          "Prozent aus wie eine Verdopplung. Bei Säulen- und Balkendiagrammen ist das " +
          "immer irreführend, weil die Länge der Säule die Aussage trägt. Bei " +
          "Liniendiagrammen ist ein Ausschnitt vertretbar – dort trägt die Neigung die " +
          "Aussage, nicht die absolute Höhe.",
      ],
      keyPoints: [
        "Das Pluszeichen rechts am Diagramm schaltet die Elemente einzeln ein und aus.",
        "Legende erst ab vier Datenreihen; darunter direkt beschriften.",
        "Bei Säulen muss die Wertachse bei null beginnen – sonst lügt die Länge.",
      ],
    },
  ],
};
